/**
 * Tabella movimenti bancari con filtri e riconciliazione manuale.
 *
 * Mostra l'elenco dei movimenti di un conto con importo colorato,
 * badge stato riconciliazione e azione per la riconciliazione manuale.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import React, { useState } from 'react';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import { useFormatters } from '@lib/formatters';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Movimento {
  id: string;
  data_contabile: string;
  data_valuta: string | null;
  importo: number;
  valuta: string;
  descrizione: string;
  controparte_nome: string | null;
  controparte_iban: string | null;
  tipo: string;
  stato: string;
  riconciliato: boolean;
  riconciliato_at: string | null;
  riferimento: string | null;
}

export interface MovimentiTableProps {
  movimenti: Movimento[];
  loading: boolean;
  onRiconcilia?: (movimentoId: string, transazioneId: string) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Determina la classe CSS per l'importo in base al segno.
 *
 * @param importo - Valore numerico del movimento (positivo = entrata, negativo = uscita).
 * @returns Classe Tailwind con colore appropriato.
 */
const classeImporto = (importo: number): string => {
  return importo >= 0
    ? 'text-green-600 dark:text-green-400'
    : 'text-red-600 dark:text-red-400';
};

/**
 * Formatta l'importo con segno esplicito e valuta.
 *
 * @param importo - Valore numerico.
 * @param valuta - Codice ISO 4217 della valuta.
 * @returns Stringa formattata con segno e simbolo.
 */
const formatImporto = (importo: number, valuta: string): string => {
  const abs = Math.abs(importo).toLocaleString('it-IT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const simbolo = valuta === 'EUR' ? '€' : valuta;
  const segno = importo >= 0 ? '+' : '−';
  return `${segno}${abs} ${simbolo}`;
};

// ---------------------------------------------------------------------------
// Sub-component: Dialog riconciliazione
// ---------------------------------------------------------------------------

interface RiconciliaDialogProps {
  movimentoId: string;
  open: boolean;
  onClose: () => void;
  onConfirm: (movimentoId: string, transazioneId: string) => void;
}

/**
 * Dialog modale per l'inserimento dell'UUID di transazione
 * da collegare manualmente a un movimento bancario.
 */
const RiconciliaDialog: React.FC<RiconciliaDialogProps> = ({
  movimentoId,
  open,
  onClose,
  onConfirm,
}: RiconciliaDialogProps) => {
  const [transazioneId, setTransazioneId] = useState('');
  const [errore, setErrore] = useState<string | null>(null);

  /** Regex UUID v4 per validazione lato client prima dell'invio. */
  const UUID_REGEX =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  const handleConfirm = (): void => {
    if (!UUID_REGEX.test(transazioneId.trim())) {
      setErrore('Inserire un UUID valido (formato: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)');
      return;
    }
    onConfirm(movimentoId, transazioneId.trim());
    setTransazioneId('');
    setErrore(null);
    onClose();
  };

  const handleClose = (): void => {
    setTransazioneId('');
    setErrore(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v: boolean) => !v && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Riconcilia movimento</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">
            Inserire l&apos;UUID della transazione core da collegare a questo movimento.
          </p>
          <Input
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            value={transazioneId}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setTransazioneId(e.target.value);
              setErrore(null);
            }}
            autoFocus
          />
          {errore && (
            <p className="text-xs text-destructive">{errore}</p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={handleClose}>
              Annulla
            </Button>
            <Button onClick={handleConfirm} disabled={!transazioneId.trim()}>
              Riconcilia
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * Tabella movimenti bancari con colorazione importo e badge riconciliazione.
 *
 * @param movimenti - Lista di movimenti da visualizzare.
 * @param loading - Indica se i dati sono in caricamento.
 * @param onRiconcilia - Callback invocata con (movimentoId, transazioneId) alla conferma.
 */
export const MovimentiTable: React.FC<MovimentiTableProps> = ({
  movimenti,
  loading,
  onRiconcilia,
}: MovimentiTableProps) => {
  const fmt = useFormatters();
  const [dialogMovimentoId, setDialogMovimentoId] = useState<string | null>(null);

  const handleRiconciliaClick = (id: string): void => {
    setDialogMovimentoId(id);
  };

  const handleDialogClose = (): void => {
    setDialogMovimentoId(null);
  };

  const handleDialogConfirm = (movimentoId: string, transazioneId: string): void => {
    onRiconcilia?.(movimentoId, transazioneId);
  };

  if (loading) {
    return (
      <p className="p-6 text-center text-sm text-muted-foreground">
        Caricamento…
      </p>
    );
  }

  return (
    <>
      {dialogMovimentoId && (
        <RiconciliaDialog
          movimentoId={dialogMovimentoId}
          open={!!dialogMovimentoId}
          onClose={handleDialogClose}
          onConfirm={handleDialogConfirm}
        />
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Data</TableHead>
            <TableHead>Descrizione</TableHead>
            <TableHead>Controparte</TableHead>
            <TableHead className="text-right">Importo</TableHead>
            <TableHead>Riconciliazione</TableHead>
            <TableHead className="text-right">Azioni</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {movimenti.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={6}
                className="text-center text-sm text-muted-foreground"
              >
                Nessun movimento trovato
              </TableCell>
            </TableRow>
          )}
          {movimenti.map((mov: Movimento) => (
            <TableRow key={mov.id}>
              {/* Data */}
              <TableCell className="text-sm">
                <span className="whitespace-nowrap">
                  {fmt.date(mov.data_contabile)}
                </span>
              </TableCell>

              {/* Descrizione */}
              <TableCell className="max-w-xs">
                <p className="truncate text-sm" title={mov.descrizione}>
                  {mov.descrizione || '—'}
                </p>
                {mov.riferimento && (
                  <p className="truncate text-xs text-muted-foreground" title={mov.riferimento}>
                    Rif: {mov.riferimento}
                  </p>
                )}
              </TableCell>

              {/* Controparte */}
              <TableCell className="text-sm">
                {mov.controparte_nome ? (
                  <span>
                    <span>{mov.controparte_nome}</span>
                    {mov.controparte_iban && (
                      <span className="block text-xs text-muted-foreground font-mono">
                        {mov.controparte_iban}
                      </span>
                    )}
                  </span>
                ) : (
                  '—'
                )}
              </TableCell>

              {/* Importo */}
              <TableCell className="text-right tabular-nums">
                <span className={`font-mono font-medium ${classeImporto(mov.importo)}`}>
                  {formatImporto(mov.importo, mov.valuta)}
                </span>
              </TableCell>

              {/* Stato riconciliazione */}
              <TableCell>
                {mov.riconciliato ? (
                  <Badge variant="default">Riconciliato</Badge>
                ) : (
                  <Badge variant="outline">Da riconciliare</Badge>
                )}
              </TableCell>

              {/* Azioni */}
              <TableCell className="text-right">
                {!mov.riconciliato && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRiconciliaClick(mov.id)}
                  >
                    Riconcilia
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
};

export default MovimentiTable;
